import React, { useState, useEffect } from 'react';
import { X, Calendar, Clock, AlertCircle, User, Image, Trash2 } from 'lucide-react';
import { useNotifications } from '../contexts/NotificationContext';
import { loadUsers, createRemind, updateRemind, deleteRemindPhoto } from '../api';

const RemindsForm = ({ onClose, onSuccess, remind = null, currentUser }) => {
  const { showNotification } = useNotifications();
  const [loading, setLoading] = useState(false);
  const [availableUsers, setAvailableUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const [selectedAgents, setSelectedAgents] = useState([]); // Array de agentes seleccionados
  const [formData, setFormData] = useState({
    nombre_cliente: '',
    descripcion: '',
    fecha_hora: '',
    prioridad: 'normal',
    agente_asignado: [], // Ahora es un array
    estado: 'pendiente'
  });
  const [fotos, setFotos] = useState([]);
  const [fotosExistentes, setFotosExistentes] = useState([]);

  // Cargar usuarios desde la base de datos
  useEffect(() => {
    const loadUsersData = async () => {
      try {
        console.log('Cargando usuarios desde la API...');
        const users = await loadUsers();
        console.log('Usuarios recibidos:', users);
        setAvailableUsers(users);
      } catch (error) {
        console.error('Error cargando usuarios:', error);
        console.error('Error details:', error.message);
      }
    };
    
    loadUsersData();
  }, []);

  useEffect(() => {
    if (remind) {
      // Parsear agentes asignados (puede venir como array o string)
      let agentesArray = [];
      if (remind.agente_asignado) {
        if (Array.isArray(remind.agente_asignado)) {
          agentesArray = remind.agente_asignado;
        } else {
          try {
            agentesArray = JSON.parse(remind.agente_asignado);
          } catch (e) {
            agentesArray = [remind.agente_asignado];
          }
        }
      }
      
      setFormData({
        nombre_cliente: remind.nombre_cliente || '',
        descripcion: remind.descripcion || '',
        fecha_hora: remind.fecha_hora ? new Date(remind.fecha_hora).toISOString().slice(0, 16) : '',
        prioridad: remind.prioridad || 'normal',
        agente_asignado: agentesArray,
        estado: remind.estado || 'pendiente'
      });
      
      setSelectedAgents(agentesArray);
      
      if (remind.fotos) {
        try {
          const fotosArray = typeof remind.fotos === 'string' ? JSON.parse(remind.fotos) : remind.fotos;
          setFotosExistentes(fotosArray || []);
        } catch (e) {
          console.error('Error parseando fotos:', e);
          setFotosExistentes([]);
        }
      }
    } else if (currentUser?.email) {
      // Si es un nuevo remind, agregar al usuario actual por defecto
      setSelectedAgents([currentUser.email]);
      setFormData(prev => ({ ...prev, agente_asignado: [currentUser.email] }));
    }
  }, [remind, currentUser]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    if (files.length + fotos.length + fotosExistentes.length > 5) {
      showNotification('Solo puedes subir un máximo de 5 fotos', 'error');
      return;
    }
    setFotos(prev => [...prev, ...files]);
  };

  const handleRemoveFoto = (index) => {
    setFotos(prev => prev.filter((_, i) => i !== index));
  };

  const handleRemoveFotoExistente = async (filename) => {
    if (!remind) return;
    
    try {
      await deleteRemindPhoto(remind.id, filename);
      setFotosExistentes(prev => prev.filter(f => f.filename !== filename));
      showNotification('Foto eliminada exitosamente', 'success');
    } catch (error) {
      console.error('Error eliminando foto:', error);
      showNotification('Error al eliminar la foto', 'error');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.nombre_cliente || !formData.descripcion || !formData.fecha_hora) {
      showNotification('Por favor completa todos los campos requeridos', 'error');
      return;
    }

    setLoading(true);

    try {
      const formDataToSend = new FormData();
      
      // Agregar campos del formulario
      Object.keys(formData).forEach(key => {
        if (key === 'agente_asignado') {
          // Enviar agentes como JSON string
          formDataToSend.append(key, JSON.stringify(formData[key]));
        } else {
          formDataToSend.append(key, formData[key]);
        }
      });
      
      // Agregar usuario que crea/actualiza
      formDataToSend.append(remind ? 'updated_by' : 'created_by', currentUser?.name || currentUser?.email || 'Sistema');
      
      // Agregar fotos nuevas
      fotos.forEach(foto => {
        formDataToSend.append('fotos', foto);
      });

      if (remind) {
        await updateRemind(remind.id, formDataToSend);
        showNotification('Remind actualizado exitosamente', 'success');
      } else {
        await createRemind(formDataToSend);
        showNotification('Remind creado exitosamente', 'success');
      }
      
      onSuccess();
      onClose();
    } catch (error) {
      console.error('Error:', error);
      showNotification(error.message || 'Error al guardar el remind', 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b px-6 py-4 flex justify-between items-center">
          <h2 className="text-xl font-semibold text-gray-800">
            {remind ? 'Editar Remind' : 'Nuevo Remind'}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X size={24} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Nombre del Cliente */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Nombre del Cliente *
            </label>
            <input
              type="text"
              name="nombre_cliente"
              value={formData.nombre_cliente}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              required
            />
          </div>

          {/* Descripción */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Descripción *
            </label>
            <textarea
              name="descripcion"
              value={formData.descripcion}
              onChange={handleChange}
              rows={4}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none"
              required
            />
          </div>

          {/* Fecha y Hora */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              <Calendar className="inline mr-1" size={16} />
              Fecha y Hora *
            </label>
            <input
              type="datetime-local"
              name="fecha_hora"
              value={formData.fecha_hora}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              required
            />
          </div>

          {/* Prioridad */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              <AlertCircle className="inline mr-1" size={16} />
              Prioridad
            </label>
            <select
              name="prioridad"
              value={formData.prioridad}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            >
              <option value="baja">Baja</option>
              <option value="normal">Normal</option>
              <option value="alta">Alta</option>
              <option value="urgente">Urgente</option>
            </select>
          </div>

          {/* Agentes Asignados (Múltiples) */}
          <div className="relative">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              <User className="inline mr-1" size={16} />
              Agentes Asignados ({selectedAgents.length})
            </label>
            
            {/* Agentes seleccionados */}
            {selectedAgents.length > 0 && (
              <div className="mb-2 flex flex-wrap gap-2">
                {selectedAgents.map((agentEmail, index) => {
                  const agent = availableUsers.find(u => u.email === agentEmail);
                  const agentName = agent ? `${agent.firstName || ''} ${agent.lastName || ''}`.trim() || agent.email : agentEmail;
                  return (
                    <div
                      key={index}
                      className="inline-flex items-center gap-1 px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-sm"
                    >
                      <span>{agentName}</span>
                      <button
                        type="button"
                        onClick={() => {
                          const newAgents = selectedAgents.filter((_, i) => i !== index);
                          setSelectedAgents(newAgents);
                          setFormData(prev => ({ ...prev, agente_asignado: newAgents }));
                        }}
                        className="hover:bg-purple-200 rounded-full p-0.5"
                      >
                        <X size={14} />
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
            
            <div className="flex gap-2">
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setShowUserDropdown(true);
                }}
                onFocus={() => setShowUserDropdown(true)}
                placeholder="Buscar agente por nombre o email..."
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
              <button
                type="button"
                onClick={() => {
                  if (currentUser && currentUser.email && !selectedAgents.includes(currentUser.email)) {
                    const newAgents = [...selectedAgents, currentUser.email];
                    setSelectedAgents(newAgents);
                    setFormData(prev => ({ ...prev, agente_asignado: newAgents }));
                    setSearchTerm('');
                  }
                }}
                className="px-4 py-2 bg-purple-100 text-purple-700 rounded-lg hover:bg-purple-200 transition-colors whitespace-nowrap font-medium"
                title="Agregarme a este remind"
              >
                Agregarme
              </button>
            </div>
            
            {/* Dropdown de usuarios */}
            {showUserDropdown && searchTerm && (
              <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                {availableUsers
                  .filter(user => {
                    const fullName = `${user.firstName || ''} ${user.lastName || ''}`.trim();
                    const userEmail = user.email || '';
                    const search = searchTerm.toLowerCase();
                    const matchesSearch = fullName.toLowerCase().includes(search) || userEmail.toLowerCase().includes(search);
                    const notSelected = !selectedAgents.includes(user.email);
                    return matchesSearch && notSelected;
                  })
                  .map(user => {
                    const fullName = `${user.firstName || ''} ${user.lastName || ''}`.trim();
                    return (
                      <div
                        key={user.id}
                        onClick={() => {
                          if (!selectedAgents.includes(user.email)) {
                            const newAgents = [...selectedAgents, user.email];
                            setSelectedAgents(newAgents);
                            setFormData(prev => ({ ...prev, agente_asignado: newAgents }));
                            setSearchTerm('');
                            setShowUserDropdown(false);
                          }
                        }}
                        className="px-3 py-2 hover:bg-purple-50 cursor-pointer border-b border-gray-100 last:border-b-0"
                      >
                        <div className="font-medium text-gray-900">{fullName || 'Sin nombre'}</div>
                        <div className="text-sm text-gray-500">{user.email || 'Sin email'}</div>
                        {user.role && (
                          <div className="text-xs text-purple-600 mt-1">
                            {user.role === 'admin' && '👑 Administrador'}
                            {user.role === 'ejecutivo_ventas' && '💼 Ejecutivo de Ventas'}
                            {user.role === 'gestion' && '📊 Gestión'}
                            {user.role === 'ejecutivo' && '💼 Ejecutivo'}
                            {user.role === 'capacitador' && '📚 Capacitador'}
                            {user.role === 'standard' && '👤 Usuario Estándar'}
                          </div>
                        )}
                      </div>
                    );
                  })}
                {availableUsers.filter(user => {
                  const fullName = `${user.firstName || ''} ${user.lastName || ''}`.trim();
                  const userEmail = user.email || '';
                  const search = searchTerm.toLowerCase();
                  const matchesSearch = fullName.toLowerCase().includes(search) || userEmail.toLowerCase().includes(search);
                  const notSelected = !selectedAgents.includes(user.email);
                  return matchesSearch && notSelected;
                }).length === 0 && (
                  <div className="px-3 py-2 text-gray-500 text-sm">
                    {selectedAgents.length > 0 ? 'No hay más agentes disponibles' : 'No se encontraron agentes'}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Estado */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Estado
            </label>
            <select
              name="estado"
              value={formData.estado}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            >
              <option value="pendiente">Pendiente</option>
              <option value="en_proceso">En Proceso</option>
              <option value="realizado">Realizado</option>
              <option value="listo">Listo</option>
            </select>
          </div>

          {/* Fotos Existentes */}
          {fotosExistentes.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Image className="inline mr-1" size={16} />
                Fotos Actuales
              </label>
              <div className="grid grid-cols-3 gap-2">
                {fotosExistentes.map((foto, index) => (
                  <div key={index} className="relative group">
                    <img
                      src={`http://localhost:3001${foto.url}`}
                      alt={foto.originalname}
                      className="w-full h-24 object-cover rounded-lg"
                    />
                    <button
                      type="button"
                      onClick={() => handleRemoveFotoExistente(foto.filename)}
                      className="absolute top-1 right-1 bg-red-500 text-white p-1 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Nuevas Fotos */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <Image className="inline mr-1" size={16} />
              Agregar Fotos (máx. 5 total)
            </label>
            <input
              type="file"
              accept="image/*"
              multiple
              onChange={handleFileChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              disabled={fotos.length + fotosExistentes.length >= 5}
            />
            {fotos.length > 0 && (
              <div className="mt-2 grid grid-cols-3 gap-2">
                {fotos.map((foto, index) => (
                  <div key={index} className="relative group">
                    <img
                      src={URL.createObjectURL(foto)}
                      alt={foto.name}
                      className="w-full h-24 object-cover rounded-lg"
                    />
                    <button
                      type="button"
                      onClick={() => handleRemoveFoto(index)}
                      className="absolute top-1 right-1 bg-red-500 text-white p-1 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Botones */}
          <div className="flex justify-end space-x-3 pt-4 border-t">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
              disabled={loading}
            >
              Cancelar
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50"
              disabled={loading}
            >
              {loading ? 'Guardando...' : (remind ? 'Actualizar' : 'Crear')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default RemindsForm;
